import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Permission } from "domain/entities/permission";
import { PermissionInputFilter } from "domain/models/permission-input-filter";
import { Logger } from "infra/logger/logger";
import permissionRepository from "infra/repositories/permission-repository";

/**
 * Hook customizado para buscar uma lista paginada e filtrada de permissões.
 * Utiliza o `useQuery` do `react-query` para gerir o estado do servidor, cache e refetching.
 *
 * @param {PermissionInputFilter} filter - O objeto de filtro para a query de permissões.
 * @returns O resultado da query do `react-query`, contendo `data`, `isLoading`, `isError`, etc.
 */
export const usePermissionByFilter = (filter: PermissionInputFilter) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["permissions", filter],
    queryFn: async () => {
      try {
        const result = await permissionRepository.getAll(filter);
        return result;
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data?.metadata?.errors?.[0],
            error.response?.data?.metadata
          );
          throw new Error(
            error.response?.data?.metadata?.errors?.[0] ||
              "An unexpected error occurred"
          );
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
  });
};

/**
 * Hook customizado para criar uma nova permissão.
 * Utiliza o `useMutation` do `react-query`. Em caso de sucesso, invalida as queries de 'permissions'
 * para garantir que a lista seja atualizada.
 * @returns A mutação do `react-query` para criar uma permissão.
 */
export const useCreatePermissionMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Permission) => {
      const result = await permissionRepository.create(data);
      return result;
    },
    onSuccess: (data) => {
      // Invalida todas as queries que começam com 'permissions' para garantir que a lista seja atualizada.
      queryClient.invalidateQueries({ queryKey: ["permissions"] });
    },
    onError: (error: unknown) => {
      if (error instanceof AxiosError) {
        if (error.response?.data?.metadata?.errors) {
          const errors = error.response.data.metadata.errors;
          logger.error(errors[0], error.response?.data.metadata);
          const customError = new Error("Erro na criação da permissão");
          (customError as any).errors = errors; // Adiciona o array de erros ao objeto de erro
          throw customError;
        }
      }
      logger.error("Erro desconhecido na criação da permissão", { error });
      throw error;
    },
  });
};

/**
 * Hook customizado para atualizar uma permissão existente.
 * Utiliza o `useMutation` do `react-query`. Em caso de sucesso, invalida as queries de 'permissions'
 * para garantir que a lista seja atualizada.
 * @param {Permission} data - O objeto de permissão com os dados atualizados.
 * @returns A mutação do `react-query` para atualizar uma permissão.
 */
export const useUpdatePermissionMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Permission) => {
      const result = await permissionRepository.update(data);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["permissions"] });
    },
    onError: (error: unknown) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data?.metadata?.errors?.[0],
          error.response?.data?.metadata
        );
        throw new Error(
          error.response?.data?.metadata?.errors?.[0] ||
            "An unexpected error occurred"
        );
      }
      logger.error("Erro desconhecido ao atualizar permissão", { error });
      throw error;
    },
  });
};

/**
 * Hook customizado para apagar uma permissão.
 * Utiliza o `useMutation` do `react-query`. Em caso de sucesso, invalida as queries de 'permissions'
 * para garantir que a lista seja atualizada.
 * @param {string} id - O ID da permissão a ser apagada.
 * @returns A mutação do `react-query` para apagar uma permissão.
 */
export const useDeletePermissionMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const result = await permissionRepository.delete(id);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["permissions"] });
    },
    onError: (error: unknown) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data?.metadata?.errors?.[0],
          error.response?.data?.metadata
        );
        throw new Error(
          error.response?.data?.metadata?.errors?.[0] ||
            "An unexpected error occurred"
        );
      }
      logger.error("Erro desconhecido ao apagar permissão", { error });
      throw error;
    },
  });
};
